import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import { trackEvent } from "../utils/trackEvent";
import "../styles/InterviewPrep.css";

interface Question {
  id?: number;
  question: string;
  category: string;
  difficulty: string;
  timeLimit?: number;
  topic?: string;
}

interface MockSession {
  sessionId: string;
  questions: Question[];
  tips: string[];
}

interface AnswerEvaluation {
  score: number;
  feedback: string[];
  strengths: string[];
  rating: string;
}

const InterviewPrep: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"questions" | "mock" | "tips">("questions");
  const [category, setCategory] = useState<string>("behavioral");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);
  
  // Mock Interview State
  const [mockSession, setMockSession] = useState<MockSession | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [evaluation, setEvaluation] = useState<AnswerEvaluation | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answers, setAnswers] = useState<Record<number, { answer: string; evaluation: AnswerEvaluation }>>({});
  
  // Job-specific questions
  const [jobDescription, setJobDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/auth/interview/questions/${category}`);
      setQuestions(response.data.questions);
    } catch (error) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    trackEvent("interview_prep_viewed");
  }, []);

  useEffect(() => {
    if (activeTab === "questions") {
      fetchQuestions();
    }
  }, [category, activeTab, fetchQuestions]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isRecording && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsRecording(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRecording, timeLeft]);

  const fetchJobSpecificQuestions = async () => {
    if (!jobDescription) {
      toast.error("Please enter a job description");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get("/auth/interview/questions-for-job", {
        params: { jobDescription, jobTitle }
      });
      setQuestions(response.data.questions);
      toast.success(`Generated ${response.data.questions.length} job-specific questions`);
    } catch (error) {
      toast.error("Failed to generate questions");
    } finally {
      setLoading(false);
    }
  };

  const startMockInterview = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post("/auth/interview/mock-session", {
        category,
        jobTitle: jobTitle || undefined,
        questionCount: 5
      });
      setMockSession(response.data);
      setCurrentQuestionIndex(0);
      setAnswers({});
      toast.success("Mock interview started! Good luck!");
    } catch (error) {
      toast.error("Failed to start mock interview");
    } finally {
      setLoading(false);
    }
  };

  const startAnswering = () => {
    if (!mockSession) return;
    setIsRecording(true);
    setTimeLeft(mockSession.questions[currentQuestionIndex].timeLimit || 120);
    setUserAnswer("");
    setEvaluation(null);
  };

  const submitAnswer = async () => {
    if (!mockSession || !userAnswer.trim()) {
      toast.error("Please provide an answer");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post("/auth/interview/evaluate-answer", {
        question: mockSession.questions[currentQuestionIndex].question,
        answer: userAnswer,
        category: mockSession.questions[currentQuestionIndex].category
      });
      setEvaluation(response.data);
      setAnswers(prev => ({
        ...prev,
        [currentQuestionIndex]: { answer: userAnswer, evaluation: response.data }
      }));
      setIsRecording(false);
    } catch (error) {
      toast.error("Failed to evaluate answer");
    } finally {
      setLoading(false);
    }
  };

  const nextQuestion = () => {
    if (!mockSession) return;
    if (currentQuestionIndex < mockSession.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setUserAnswer("");
      setEvaluation(null);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      const prevAnswer = answers[currentQuestionIndex - 1];
      if (prevAnswer) {
        setUserAnswer(prevAnswer.answer);
        setEvaluation(prevAnswer.evaluation);
      } else {
        setUserAnswer("");
        setEvaluation(null);
      }
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "#22c55e";
    if (score >= 60) return "#f59e0b";
    return "#ef4444";
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const renderQuestionsTab = () => (
    <div className="ip-tab-content">
      {/* Category Selection */}
      <div className="ip-category-section">
        <h3>Question Categories</h3>
        <div className="ip-categories">
          {["behavioral", "technical", "situational", "cultural"].map(cat => (
            <button
              key={cat}
              className={`ip-category-btn ${category === cat ? "active" : ""}`}
              onClick={() => setCategory(cat)}
            >
              {cat === "behavioral" && "🧠 "}
              {cat === "technical" && "💻 "}
              {cat === "situational" && "🎯 "}
              {cat === "cultural" && "🤝 "}
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Job-Specific Questions */}
      <div className="ip-job-specific">
        <h3>📋 Generate Job-Specific Questions</h3>
        <input
          type="text"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          placeholder="Job Title (e.g., Senior Software Engineer)"
          className="ip-input"
        />
        <textarea
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          placeholder="Paste the job description here..."
          className="ip-textarea"
          rows={3}
        />
        <button onClick={fetchJobSpecificQuestions} className="ip-generate-btn" disabled={loading}>
          {loading ? "Generating..." : "Generate Questions"}
        </button>
      </div>

      {/* Questions List */}
      <div className="ip-questions-list">
        <h3>📝 Practice Questions ({questions.length})</h3>
        {loading ? (
          <div className="ip-loading">Loading questions...</div>
        ) : (
          <div className="ip-questions">
            {questions.map((q, idx) => (
              <div key={idx} className="ip-question-card">
                <div 
                  className="ip-question-header"
                  onClick={() => setExpandedQuestion(expandedQuestion === idx ? null : idx)}
                >
                  <span className="ip-question-number">Q{idx + 1}</span>
                  <span className="ip-question-text">{q.question}</span>
                  <span className={`ip-difficulty ${q.difficulty.toLowerCase()}`}>
                    {q.difficulty}
                  </span>
                </div>
                {expandedQuestion === idx && (
                  <div className="ip-question-tips">
                    <p><strong>Category:</strong> {q.category || q.topic}</p>
                    <p><strong>Time Limit:</strong> {q.timeLimit || 120} seconds</p>
                    <p><strong>Tips:</strong></p>
                    <ul>
                      <li>Use the STAR method (Situation, Task, Action, Result)</li>
                      <li>Be specific with examples</li>
                      <li>Keep your answer to 1-2 minutes</li>
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderMockTab = () => (
    <div className="ip-tab-content">
      {!mockSession ? (
        <div className="ip-mock-setup">
          <h3>🎤 Start a Mock Interview</h3>
          <p>Practice answering interview questions with AI feedback.</p>
          
          <div className="ip-mock-options">
            <label>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="ip-select">
              <option value="behavioral">Behavioral</option>
              <option value="technical">Technical</option>
              <option value="situational">Situational</option>
              <option value="cultural">Cultural Fit</option>
            </select>

            <label>Job Title (optional)</label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g., Software Engineer"
              className="ip-input"
            />
          </div>

          <button onClick={startMockInterview} className="ip-start-btn" disabled={loading}>
            {loading ? "Starting..." : "🚀 Start Mock Interview"}
          </button>
        </div>
      ) : (
        <div className="ip-mock-session">
          {/* Progress */}
          <div className="ip-progress">
            <span>Question {currentQuestionIndex + 1} of {mockSession.questions.length}</span>
            <div className="ip-progress-bar">
              <div 
                className="ip-progress-fill"
                style={{ width: `${((currentQuestionIndex + 1) / mockSession.questions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <div className="ip-current-question">
            <span className={`ip-difficulty ${mockSession.questions[currentQuestionIndex].difficulty.toLowerCase()}`}>
              {mockSession.questions[currentQuestionIndex].difficulty}
            </span>
            <h3>{mockSession.questions[currentQuestionIndex].question}</h3>
            <p className="ip-question-category">
              Category: {mockSession.questions[currentQuestionIndex].category}
            </p>
          </div>

          {/* Timer */}
          {isRecording && (
            <div className={`ip-timer ${timeLeft <= 30 ? "warning" : ""}`}>
              ⏱️ {formatTime(timeLeft)}
            </div>
          )}

          {/* Answer Input */}
          <div className="ip-answer-section">
            {!evaluation ? (
              <>
                <textarea
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder={isRecording ? "Type your answer here..." : "Click 'Start Answering' to begin"}
                  className="ip-answer-input"
                  disabled={!isRecording}
                  rows={6}
                />
                <div className="ip-answer-actions">
                  {!isRecording ? (
                    <button onClick={startAnswering} className="ip-record-btn">
                      ▶️ Start Answering
                    </button>
                  ) : (
                    <button onClick={submitAnswer} className="ip-submit-btn" disabled={loading || !userAnswer.trim()}>
                      {loading ? "Evaluating..." : "📤 Submit Answer"}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="ip-evaluation">
                <div className="ip-eval-header">
                  <div 
                    className="ip-eval-score"
                    style={{ backgroundColor: getScoreColor(evaluation.score) }}
                  >
                    {evaluation.score}/100
                  </div>
                  <span className="ip-eval-rating">{evaluation.rating}</span>
                </div>

                {evaluation.strengths.length > 0 && (
                  <div className="ip-eval-section">
                    <h4>✅ Strengths</h4>
                    <ul>
                      {evaluation.strengths.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}

                {evaluation.feedback.length > 0 && (
                  <div className="ip-eval-section">
                    <h4>💡 Suggestions</h4>
                    <ul>
                      {evaluation.feedback.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="ip-nav-actions">
            <button onClick={prevQuestion} disabled={currentQuestionIndex === 0} className="ip-nav-btn">
              ← Previous
            </button>
            <button 
              onClick={() => setMockSession(null)} 
              className="ip-end-btn"
            >
              End Session
            </button>
            <button 
              onClick={nextQuestion} 
              disabled={currentQuestionIndex >= mockSession.questions.length - 1}
              className="ip-nav-btn"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderTipsTab = () => (
    <div className="ip-tab-content">
      <div className="ip-tips-section">
        <h3>🎯 Interview Success Tips</h3>
        
        <div className="ip-tips-grid">
          <div className="ip-tip-card">
            <h4>📝 The STAR Method</h4>
            <p>Structure your behavioral answers using:</p>
            <ul>
              <li><strong>S</strong>ituation: Set the context</li>
              <li><strong>T</strong>ask: Describe your responsibility</li>
              <li><strong>A</strong>ction: Explain what you did</li>
              <li><strong>R</strong>esult: Share the outcome</li>
            </ul>
          </div>

          <div className="ip-tip-card">
            <h4>⏱️ Timing</h4>
            <ul>
              <li>Keep answers to 1-2 minutes</li>
              <li>Pause to think - silence is okay</li>
              <li>Don't rush through important details</li>
            </ul>
          </div>

          <div className="ip-tip-card">
            <h4>🔢 Quantify Achievements</h4>
            <ul>
              <li>Use numbers and percentages</li>
              <li>"Increased efficiency by 30%"</li>
              <li>"Led a team of 5 developers"</li>
              <li>"Reduced costs by $50,000"</li>
            </ul>
          </div>

          <div className="ip-tip-card">
            <h4>❓ Ask Questions</h4>
            <p>Prepare thoughtful questions:</p>
            <ul>
              <li>About the team structure</li>
              <li>About growth opportunities</li>
              <li>About company culture</li>
              <li>About the role's challenges</li>
            </ul>
          </div>

          <div className="ip-tip-card">
            <h4>🎭 Body Language</h4>
            <ul>
              <li>Maintain eye contact</li>
              <li>Sit up straight</li>
              <li>Use hand gestures naturally</li>
              <li>Smile genuinely</li>
            </ul>
          </div>

          <div className="ip-tip-card">
            <h4>📚 Research</h4>
            <ul>
              <li>Know the company's mission</li>
              <li>Understand the role requirements</li>
              <li>Research your interviewers</li>
              <li>Know industry trends</li>
            </ul>
          </div>
        </div>

        <div className="ip-common-questions">
          <h3>🔥 Most Common Questions</h3>
          <div className="ip-common-list">
            <div className="ip-common-item">
              <span className="ip-common-number">1</span>
              <span>"Tell me about yourself."</span>
            </div>
            <div className="ip-common-item">
              <span className="ip-common-number">2</span>
              <span>"Why do you want to work here?"</span>
            </div>
            <div className="ip-common-item">
              <span className="ip-common-number">3</span>
              <span>"What's your greatest strength?"</span>
            </div>
            <div className="ip-common-item">
              <span className="ip-common-number">4</span>
              <span>"What's your greatest weakness?"</span>
            </div>
            <div className="ip-common-item">
              <span className="ip-common-number">5</span>
              <span>"Where do you see yourself in 5 years?"</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ip-container">
      <div className="ip-header">
        <button onClick={() => navigate("/dashboard")} className="ip-back-btn">
          ← Back to Dashboard
        </button>
        <h1>🎤 Interview Preparation</h1>
        <p>Practice questions, mock interviews, and tips for success</p>
      </div>

      {/* Tabs */}
      <div className="ip-tabs">
        <button 
          className={`ip-tab ${activeTab === "questions" ? "active" : ""}`}
          onClick={() => setActiveTab("questions")}
        >
          📝 Questions
        </button>
        <button 
          className={`ip-tab ${activeTab === "mock" ? "active" : ""}`}
          onClick={() => setActiveTab("mock")}
        >
          🎤 Mock Interview
        </button>
        <button 
          className={`ip-tab ${activeTab === "tips" ? "active" : ""}`}
          onClick={() => setActiveTab("tips")}
        >
          💡 Tips
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "questions" && renderQuestionsTab()}
      {activeTab === "mock" && renderMockTab()}
      {activeTab === "tips" && renderTipsTab()}
    </div>
  );
};

export default InterviewPrep;
